import { LeaguePreviewCardProps } from '@dreamteam-monorepo/ui';

import engFlag from '@/assets/teams/flags/eng.png';
import sctFlag from '@/assets/teams/flags/sct.png';
import wlsFlag from '@/assets/teams/flags/wls.png';
import nirFlag from '@/assets/teams/flags/nir.png';
import irlFlag from '@/assets/teams/flags/irl.png';

import arsenalBadge from '@/assets/teams/badges/arsenal.png';
import astonVillaBadge from '@/assets/teams/badges/aston-villa.png';
import afcBournemouthBadge from '@/assets/teams/badges/afc-bournemouth.png';
import brentfordBadge from '@/assets/teams/badges/brentford.png';
import brightonBadge from '@/assets/teams/badges/brighton-hove-albion.png';
import burnleyBadge from '@/assets/teams/badges/burnley.png';
import chelseaBadge from '@/assets/teams/badges/chelsea.png';
import crystalPalaceBadge from '@/assets/teams/badges/crystal-palace.png';
import evertonBadge from '@/assets/teams/badges/everton.png';
import fulhamBadge from '@/assets/teams/badges/fulham.png';
import ipswichBadge from '@/assets/teams/badges/ipswich-town.png';
import liverpoolBadge from '@/assets/teams/badges/liverpool.png';
import lutonBadge from '@/assets/teams/badges/luton-town.png';
import manchesterCityBadge from '@/assets/teams/badges/manchester-city.png';
import manchesterUnitedBadge from '@/assets/teams/badges/manchester-united.png';
import newcastleBadge from '@/assets/teams/badges/newcastle-united.png';
import nottinghamForestBadge from '@/assets/teams/badges/nottingham-forest.png';
import sheffieldUnitedBadge from '@/assets/teams/badges/sheffield-united.png';
import southamptonBadge from '@/assets/teams/badges/southampton.png';
import tottenhamBadge from '@/assets/teams/badges/tottenham-hotspur.png';
import westHamBadge from '@/assets/teams/badges/west-ham-united.png';
import wolverhamptonBadge from '@/assets/teams/badges/wolverhampton-wanderers.png';

export interface BELeague {
  id: string;
  name: string;
  description?: string | null;
  type: 'mini_league' | 'club' | 'national';
  club_id?: string | null;
  country_code?: string | null;
  start_gameweek: number;
  is_public: boolean;
  is_celebrity: boolean;
  roll_over_league: boolean;
  join_code: string;
  tournament_calendar_id: string | null;
  commissioner_user_id: string | null;
  commissioner_username: string | null;
  member_count: number;
  created_at: string;
  updated_at: string;
  user_rank: number | null;
  user_points: string | null;
}

export interface BELeagueEntry {
  id: string;
  league_id: string;
  user_id: string;
  fantasy_team_id: string;
  joined_at: string;
  rank: number | null;
  points: string;
  user?: {
    id: string;
    ref_id: string;
    username: string | null;
    ref_country_code: string | null;
  };
  fantasy_team?: {
    id: string;
    team_name: string;
    formation: string | null;
  };
}

// Maps club_id strings (as stored in DB) to badge image URLs.
// club_id is the club name exactly as seeded (e.g. "Arsenal", "Brighton & Hove Albion").
const CLUB_BADGE_MAP: Record<string, string> = {
  Arsenal: arsenalBadge,
  'Aston Villa': astonVillaBadge,
  Bournemouth: afcBournemouthBadge,
  Brentford: brentfordBadge,
  'Brighton & Hove Albion': brightonBadge,
  Burnley: burnleyBadge,
  Chelsea: chelseaBadge,
  'Crystal Palace': crystalPalaceBadge,
  Everton: evertonBadge,
  Fulham: fulhamBadge,
  'Ipswich Town': ipswichBadge,
  Liverpool: liverpoolBadge,
  'Luton Town': lutonBadge,
  'Manchester City': manchesterCityBadge,
  'Manchester United': manchesterUnitedBadge,
  'Newcastle United': newcastleBadge,
  'Nottingham Forest': nottinghamForestBadge,
  'Sheffield United': sheffieldUnitedBadge,
  Southampton: southamptonBadge,
  'Tottenham Hotspur': tottenhamBadge,
  'West Ham United': westHamBadge,
  'Wolverhampton Wanderers': wolverhamptonBadge,
};

export function getClubBadge(clubId: string | null | undefined): string | undefined {
  if (!clubId) return undefined;
  return CLUB_BADGE_MAP[clubId];
}

// Maps country_code (3-char, uppercase) to flag image URLs.
const COUNTRY_FLAG_MAP: Record<string, string> = {
  ENG: engFlag,
  SCT: sctFlag,
  WLS: wlsFlag,
  NIR: nirFlag,
  IRL: irlFlag,
};

export function getCountryFlag(countryCode: string | null | undefined): string | undefined {
  if (!countryCode) return undefined;
  return COUNTRY_FLAG_MAP[countryCode.toUpperCase()];
}

const LEAGUE_TYPE_DISPLAY_PRIORITY: Record<BELeague['type'], number> = {
  national: 0,
  club: 1,
  mini_league: 2,
};

export function orderLeaguesForDisplay(leagues: readonly BELeague[]): BELeague[] {
  return [...leagues].sort(
    (left, right) =>
      LEAGUE_TYPE_DISPLAY_PRIORITY[left.type] - LEAGUE_TYPE_DISPLAY_PRIORITY[right.type]
  );
}

type CardType = NonNullable<LeaguePreviewCardProps['type']>;

const TYPE_MAP: Record<BELeague['type'], CardType> = {
  mini_league: 'generic',
  club: 'generic',
  national: 'generic',
};

export function leagueToCardProps(
  league: BELeague
): Omit<LeaguePreviewCardProps, 'onViewLeague' | 'image'> {
  return {
    type: TYPE_MAP[league.type],
    name: league.name,
    rank: league.user_rank != null ? String(league.user_rank) : '-',
    gwStart: `GW ${league.start_gameweek || 1}`,
  };
}
